import React, { useEffect, useState } from "react";
import { FormControl, Grid, InputLabel, MenuItem, Select, Snackbar, TextField } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Modal from "../Modal";
import config from "../../config";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import Lottery from "../../entities/Lottery";
import LotteryCard from "./LotteryCard";
import { useAuth } from "../../context/Auth";

const useStyles = makeStyles(({ spacing }) =>
    createStyles({
        root: {
            "& > *": {
                margin: spacing(1),
                width: `calc(50% - ${spacing(2)}px)`,
            },
        },
        fullWidth: {
            width: `calc(100% - ${spacing(2)}px)`,
        },
    })
);

const isEdit = (lottery: Lottery) => {
    return lottery && lottery.lotteryID;
};

const getUrl = (lottery: Lottery) => {
    if (isEdit(lottery)) return `${config.apiUrl}/LiveLottery/UpdateLottery`;
    return `${config.apiUrl}/LiveLottery/AddLottery`;
};

const LotteryEditor = ({ lottery, onSave, ...props }: any) => {
    const [data, setData] = useState<any>(lottery);
    const [preview] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const { fetchWithUser } = useAuth()
    const classes = useStyles();

    const setValue = (key: string, value: any) => {
        setData({
            ...data,
            [key]: value,
        });
    };

    useEffect(() => {
        const { ...fields } = lottery;
        setData(fields);
    }, [lottery]);

    const onSubmit = () => {
        const url = getUrl(data);
        // Read-only fields from GetLotteriesByEventID; the server does not save them.
        const { isEnded, isCanDeleted, lotteryWinners, ...body } = data;
        setSaving(true);
        fetchWithUser(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then(() => onSave(data))
            .catch(() => setSaveError(true))
            .finally(() => setSaving(false));
    };

    return (
        <>
        <Modal
            maxWidth="md"
            title={isEdit(lottery) ? "Edit Raffle" : "Add Raffle"}
            onSave={onSubmit}
            disabled={saving}
            {...props}
        >
            <Grid container>
                <Grid>
                    <form className={classes.root}>
                        <TextField
                            name="lotteryEnglishName"
                            variant="outlined"
                            label="Raffle Name"
                            defaultValue={data.lotteryEnglishName}
                            onChange={(e) => setValue("lotteryEnglishName", e.target.value)}
                        />

                        <TextField
                            dir="rtl"
                            name="lotteryHebrewName"
                            variant="outlined"
                            label="שם"
                            defaultValue={data.lotteryHebrewName}
                            onChange={(e) => setValue("lotteryHebrewName", e.target.value)}
                        />

                        <TextField
                            variant="outlined"
                            multiline
                            label="Raffle Description"
                            name="lotteryEnglishDesc"
                            defaultValue={data.lotteryEnglishDesc}
                            onChange={(e) => setValue("lotteryEnglishDesc", e.target.value)}
                        />

                        <TextField
                            dir="rtl"
                            variant="outlined"
                            label="תיאור"
                            multiline
                            name="lotteryHebrewDesc"
                            defaultValue={data.lotteryHebrewDesc}
                            onChange={(e) => setValue("lotteryHebrewDesc", e.target.value)}
                        />

                        <FormControl variant="outlined" fullWidth={true}>
                            <InputLabel>Raffle Type</InputLabel>
                            <Select
                                value={data.lotteryTypeID}
                                onChange={(e: any) => setValue("lotteryTypeID", e.target.value)}
                                label="Raffle Type"
                            >
                                <MenuItem value={1}>Live</MenuItem>
                                <MenuItem value={2}>Offline</MenuItem>
                                <MenuItem value={3}>Special</MenuItem>
                                <MenuItem value={4}>Monthly - Gift Card</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            dir="rtl"
                            variant="outlined"
                            label="Raffels Amount"
                            multiline
                            type="number"
                            name="lotteriesAmount"
                            defaultValue={data.lotteriesAmount}
                            onChange={(e) => setValue("lotteriesAmount", e.target.value)}
                        />

                        <TextField
                            name="lotteryNumber"
                            variant="outlined"
                            label="Raffle Number"
                            defaultValue={data.lotteryNumber}
                            onChange={(e) => setValue("lotteryNumber", e.target.value)}
                        />

                        <TextField
                            name="order"
                            variant="outlined"
                            label="Order"
                            defaultValue={data.order}
                            onChange={(e) => setValue("order", e.target.value)}
                        />
                    </form>
                </Grid>
                {preview && (
                    <Grid>
                        <LotteryCard lottery={data}></LotteryCard>
                    </Grid>
                )}
            </Grid>
        </Modal>
        <Snackbar open={saveError} autoHideDuration={6000} onClose={() => setSaveError(false)}>
            <Alert severity="error" onClose={() => setSaveError(false)}>
                The raffle was not saved. Please try again.
            </Alert>
        </Snackbar>
        </>
    );
};

export default LotteryEditor;